import { motion } from 'framer-motion'
import { ArrowLeft, Calendar, User, Tag } from 'lucide-react'
import { Link } from 'react-router-dom'

const STEMWorkshopsBlogPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <Link 
              to="/blogs" 
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blogs
            </Link>
            
            <div className="flex items-center justify-center space-x-4 mb-6 text-sm text-gray-600">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                Dec 10, 2024
              </div>
              <div className="flex items-center">
                <User className="h-4 w-4 mr-1" />
                STEM Bears Team
              </div>
              <div className="flex items-center">
                <Tag className="h-4 w-4 mr-1" />
                Workshops
              </div>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              STEM Workshops: What to Expect
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our STEM workshops are designed to spark curiosity, creativity, and innovation in young minds. Whether your child is new to science or already loves to build and experiment, these sessions provide a fun and engaging way to explore technology, coding, and engineering through real-world projects.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                At STEM Bears, we believe every child has the power to become a creator not just a consumer of technology. Our hands-on workshops make complex topics simple, enjoyable, and deeply inspiring.
              </p>
            </motion.div>

            {/* Video Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <video
                  src="/videos/STEM-Blog.mp4"
                  className="w-full h-auto"
                  controls
                  poster="/videos/STEM-Blog-cover.mp4"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </motion.div>

            {/* What Happens in a Workshop */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">What Happens in a Workshop</h2>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">1. Interactive Learning</h3>
                  <p className="text-lg text-gray-700">
                    Each session begins with an introduction to an exciting STEM concept such as robotics, AI, circuits, or app design. Kids learn how these technologies work and how they impact everyday life.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">2. Hands-On Projects</h3>
                  <p className="text-lg text-gray-700 mb-4">
                    Children apply what they learn by working on guided, age-appropriate projects such as:
                  </p>
                  <ul className="list-disc list-inside text-lg text-gray-700 space-y-2">
                    <li>Building a robot that moves and responds to commands</li>
                    <li>Coding simple games and animations</li>
                    <li>Designing electronic circuits and sensors</li>
                    <li>Exploring science experiments related to motion, light, and sound</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">3. Collaboration and Creativity</h3>
                  <p className="text-lg text-gray-700">
                    Workshops encourage teamwork, idea-sharing, and innovation. Students brainstorm solutions, test their ideas, and see their projects come to life, building both confidence and problem-solving skills.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">4. Presentation and Reflection</h3>
                  <p className="text-lg text-gray-700">
                    At the end of each workshop, participants showcase their creations and discuss what they learned. This reinforces key concepts and helps them take pride in their achievements.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Why Parents Choose STEM Bears */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Parents Choose STEM Bears</h2>
              <ul className="list-disc list-inside text-lg text-gray-700 space-y-3">
                <li><strong>All Materials Included:</strong> We provide every component needed for the projects.</li>
                <li><strong>Expert Instructors:</strong> Classes are led by experienced educators and Engineers who specialize in STEM learning for kids.</li>
                <li><strong>Structured Curriculum:</strong> Each workshop builds foundational understanding while keeping learning fun and interactive.</li>
                <li><strong>Small Group Size:</strong> Personalized attention ensures every child stays engaged and supported.</li>
              </ul>
            </motion.div>

            {/* What Kids Gain */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">What Kids Gain</h2>
              <ul className="list-disc list-inside text-lg text-gray-700 space-y-2">
                <li>A strong introduction to STEM fields like robotics, AI, and coding</li>
                <li>Real-world problem-solving and critical thinking skills</li>
                <li>Improved creativity, focus, and collaboration</li>
                <li>Confidence to explore and innovate independently</li>
              </ul>
            </motion.div>

            {/* Industry Leaders Quotes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="mb-12 bg-gradient-to-r from-green-50 to-blue-50 p-8 rounded-2xl"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">What Industry Leaders Say</h2>
              <div className="space-y-6">
                <blockquote className="text-lg text-gray-700 italic border-l-4 border-green-600 pl-6">
                  "Learning to code teaches you how to think."
                  <footer className="text-green-600 font-semibold mt-2">— Bill Gates, Co-founder of Microsoft</footer>
                </blockquote>
                <blockquote className="text-lg text-gray-700 italic border-l-4 border-green-600 pl-6">
                  "Kids who learn STEM early don't just adapt to the future, they build it."
                  <footer className="text-green-600 font-semibold mt-2">— Sundar Pichai, CEO of Google</footer>
                </blockquote>
              </div>
            </motion.div>

            {/* Call to Action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
              className="text-center bg-gradient-to-r from-green-600 to-blue-600 text-white p-8 rounded-2xl"
            >
              <h2 className="text-3xl font-bold mb-4">Ready to Enroll?</h2>
              <p className="text-xl mb-6">
                Reserve your spot today. Spaces are limited to ensure a high-quality learning experience.
              </p>
              <Link 
                to="/programs"
                className="inline-flex items-center bg-white text-green-600 hover:bg-gray-50 font-bold text-lg px-8 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Reserve Your Spot
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default STEMWorkshopsBlogPage
